/**
 * SlotUploadModal — two-step upload for document slots whose category carries
 * metadata the file cannot (UPLOAD_METADATA_FIELDS: when a post photo was
 * taken; the training date + trainer name behind a certificate).
 *
 *   step 1  pick the file(s) via a hidden <input type=file>
 *   step 2  fill in the category's fields (required ones block Confirm;
 *           dates never in the future; "date taken" never before the incident)
 *   Confirm → onConfirm({ files, metadata })  — ONE metadata object per batch
 *             (files chosen together share it; the caller stores it per file)
 *
 * Same seam and styling as TypedTextFileModal. Used by the wizard's required
 * slots and ad-hoc picker, the case-file slots, and the comment composer.
 * Spec: required_evidence_logic.md §2.
 *
 * Accessibility: a modal dialog — focus moves to the first control on open,
 * Tab / Shift+Tab cycle inside the dialog, Escape cancels, and focus returns
 * to the element that opened it when it closes.
 *
 * Props: { open, category, label,
 *          fileNamePreview?  — the upload's BASE name without extension
 *                              (e.g. 'AE123-PostPhoto'); shown as "Saved as
 *                              <base><ext>" once exactly one file is chosen,
 *          multiple? (default true),
 *          incidentDate? ('YYYY-MM-DD' | null),
 *          zIndex? (default 400 — pass higher when opened over another overlay),
 *          onCancel(), onConfirm({ files, metadata }) }
 */

import React, { useEffect, useRef, useState } from 'react';
import DateInput, { todayIso } from './DateInput.jsx';
import { CharCountInput } from './CharCountField.jsx';
import { UPLOAD_METADATA_FIELDS } from '../lib/eventVocabularies.js';
import { ACCEPT_ATTR, validateUpload } from '../lib/uploadLimits.js';

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
// Keyboard-reachable controls inside the dialog (the hidden file input is
// display:none and drops out via the offsetParent check).
const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]';

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExt(name = '') {
  const m = /\.[^.]+$/.exec(String(name));
  return m ? m[0] : '';
}

function focusableIn(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(el =>
    !el.disabled && el.tabIndex !== -1 && el.getAttribute('aria-hidden') !== 'true' && el.offsetParent !== null,
  );
}

/** Range for a date field: never in the future; "date taken" not before the incident. */
function dateBounds(field, incidentDate) {
  const max = todayIso();
  const min = field.key === 'takenOn' && incidentDate && ISO_RE.test(incidentDate) ? incidentDate : undefined;
  return { min, max };
}

function fieldProblem(field, value, incidentDate) {
  const v = typeof value === 'string' ? value.trim() : value;
  if (!v) return field.required ? 'required' : null;
  if (field.type === 'date') {
    if (!ISO_RE.test(v)) return 'invalid';
    const { min, max } = dateBounds(field, incidentDate);
    if (max && v > max) return 'future';
    if (min && v < min) return 'before-incident';
  }
  if (field.type === 'text' && field.max && v.length > field.max) return 'too-long';
  return null;
}

export default function SlotUploadModal({
  open,
  category,
  label,
  fileNamePreview,
  multiple = true,
  incidentDate = null,
  zIndex = 400,
  onCancel,
  onConfirm,
}) {
  const [files,     setFiles]     = useState([]);
  const [values,    setValues]    = useState({});
  const [fileError, setFileError] = useState(null);
  const inputRef  = useRef(null);
  const dialogRef = useRef(null);
  // Latest onCancel without re-subscribing the document listener every render.
  const onCancelRef = useRef(onCancel);
  useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);
  const fields = UPLOAD_METADATA_FIELDS[category] || [];

  // Fresh state every time the modal opens (or is re-pointed at a category).
  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setValues({});
    setFileError(null);
  }, [open, category]);

  // Modal keyboard contract: remember the opener, move focus inside, keep Tab
  // cycling within the dialog, Escape cancels; hand focus back on close.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    focusableIn(dialogRef.current)[0]?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') { onCancelRef.current?.(); return; }
      if (e.key !== 'Tab') return;
      const items = focusableIn(dialogRef.current);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last  = items[items.length - 1];
      const inside = dialogRef.current?.contains(document.activeElement);
      if (e.shiftKey) {
        if (!inside || document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else if (!inside || document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [open]);

  if (!open) return null;

  function onFilesChosen(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    const accepted = [];
    const rejected = [];
    for (const f of picked) {
      const problem = validateUpload(f.name, f.size);
      if (problem) rejected.push(`${f.name}: ${problem}`);
      else accepted.push(f);
    }
    setFileError(rejected.length ? rejected.join(' ') : null);
    if (!accepted.length) return;
    setFiles(prev => (multiple ? [...prev, ...accepted] : accepted.slice(0, 1)));
  }

  function removeFile(i) {
    setFiles(prev => prev.filter((_, idx) => idx !== i));
  }

  const problems = Object.fromEntries(fields.map(f => [f.key, fieldProblem(f, values[f.key], incidentDate)]));
  const metaOk   = fields.every(f => !problems[f.key]);
  const ready    = files.length > 0 && metaOk;
  // Exact only for a single file — with several the per-file -2/-3 numbering
  // depends on the caller's existing attachments, so say nothing rather than
  // something wrong (the caller's queued-file list shows the final names).
  const savedAs  = fileNamePreview && files.length === 1 ? `${fileNamePreview}${fileExt(files[0].name)}` : null;

  function confirm() {
    if (!ready) return;
    let metadata = null;
    if (fields.length) {
      metadata = {};
      for (const f of fields) {
        const raw = values[f.key];
        const v = typeof raw === 'string' ? raw.trim() : raw;
        if (v) metadata[f.key] = v;
      }
    }
    onConfirm?.({ files, metadata });
  }

  const problemText = (field) => {
    switch (problems[field.key]) {
      case 'future':          return 'Cannot be in the future.';
      case 'before-incident': return 'Cannot be before the date of incident.';
      case 'too-long':        return `Keep it under ${field.max} characters.`;
      case 'invalid':         return 'Enter a valid date.';
      default:                return null;
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      style={{ ...overlayStyle, zIndex }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div ref={dialogRef} style={dialogStyle}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 14 }}>
          {multiple ? 'Choose the file(s), then fill in the details below — they are saved with each file.'
                    : 'Choose the file, then fill in the details below — they are saved with it.'}
          {savedAs && (
            <> Saved as <span style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>{savedAs}</span>.</>
          )}
        </div>

        {/* ── Step 1 · files ── */}
        <div style={stepHeadStyle}>
          <span style={stepNumStyle(true)}>1</span>
          <span>{multiple ? 'Files' : 'File'}</span>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={ACCEPT_ATTR}
          style={{ display: 'none' }}
          tabIndex={-1}
          onChange={onFilesChosen}
        />
        {files.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} style={fileRowStyle}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ width: 13, height: 13, color: 'var(--brand)', flexShrink: 0 }}>
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{formatBytes(f.size)}</div>
                </div>
                <button type="button" onClick={() => removeFile(i)} title="Remove" aria-label={`Remove ${f.name}`} style={removeBtnStyle}>×</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <button
            type="button"
            className={files.length ? 'btn' : 'btn primary'}
            onClick={() => inputRef.current?.click()}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
              <path d="M12 5v14M5 12h14"/>
            </svg>
            {files.length === 0 ? (multiple ? 'Choose file(s)…' : 'Choose file…') : (multiple ? 'Add more…' : 'Choose a different file…')}
          </button>
          {files.length > 0 && (
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{files.length} file{files.length > 1 ? 's' : ''} chosen</span>
          )}
        </div>
        {fileError && <div className="field-error" style={{ marginTop: -8, marginBottom: 12 }}>{fileError}</div>}

        {/* ── Step 2 · details ── */}
        {fields.length > 0 && (
          <>
            <div style={{ ...stepHeadStyle, opacity: files.length ? 1 : 0.5 }}>
              <span style={stepNumStyle(files.length > 0)}>2</span>
              <span>Details</span>
            </div>
            {files.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>Choose the file{multiple ? '(s)' : ''} first.</div>
            ) : (
              <div className="field-row">
                {fields.map(field => {
                  const value = values[field.key] ?? '';
                  const err   = problemText(field);
                  const { min, max } = field.type === 'date' ? dateBounds(field, incidentDate) : {};
                  return (
                    <div className="field" key={field.key} style={{ marginBottom: 0 }}>
                      <label htmlFor={`slot-meta-${field.key}`}>
                        {field.label}{field.required && <> <span style={{ color: 'var(--danger)' }}>*</span></>}
                      </label>
                      {field.type === 'date' ? (
                        <DateInput
                          id={`slot-meta-${field.key}`}
                          name={field.key}
                          value={value}
                          min={min}
                          max={max}
                          popoverZIndex={Math.max(10001, zIndex + 1)}
                          onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                          style={err ? errorBorder : undefined}
                        />
                      ) : (
                        <CharCountInput
                          id={`slot-meta-${field.key}`}
                          name={field.key}
                          type="text"
                          limit={field.max}
                          value={value}
                          onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                          style={err ? errorBorder : undefined}
                        />
                      )}
                      {err && <div className="field-error">{err}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn primary" disabled={!ready} onClick={confirm}>
            {files.length > 1 ? `Attach ${files.length} files` : 'Attach'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400,
};
const dialogStyle = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
  padding: 20, width: 'min(560px, calc(100vw - 32px))',
  maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
};
const stepHeadStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em',
  color: 'var(--text-3)', marginBottom: 8,
};
const stepNumStyle = (active) => ({
  display: 'inline-grid', placeItems: 'center',
  width: 20, height: 20, borderRadius: '50%', fontSize: 11, fontWeight: 600,
  background: active ? 'var(--brand-weak)' : 'var(--surface-2)',
  color: active ? 'var(--brand-ink)' : 'var(--text-3)',
});
const fileRowStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '6px 10px', borderRadius: 8,
  background: 'var(--surface-2)', border: '1px solid var(--border)',
};
const removeBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text-3)', fontSize: 16, lineHeight: 1, padding: '0 4px',
};
const errorBorder = { borderColor: 'var(--danger)', boxShadow: '0 0 0 3px rgba(220,38,38,.12)' };
